// PWA Install Prompt Component
// Shows platform-specific install instructions
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { usePWAInstall } from '../../hooks/usePWAInstall';

// iOS Share icon SVG
function ShareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 50 50" fill="currentColor">
      <path d="M30.3 13.7L25 8.4l-5.3 5.3-1.4-1.4L25 5.6l6.7 6.7z" />
      <path d="M24 7h2v21h-2z" />
      <path d="M35 40H15c-1.7 0-3-1.3-3-3V19c0-1.7 1.3-3 3-3h7v2h-7c-.6 0-1 .4-1 1v18c0 .6.4 1 1 1h20c.6 0 1-.4 1-1V19c0-.6-.4-1-1-1h-7v-2h7c1.7 0 3 1.3 3 3v18c0 1.7-1.3 3-3 3z" />
    </svg>
  );
}

interface InstallPromptProps {
  delayMs?: number; // Delay before showing prompt
}

export default function InstallPrompt({ delayMs = 3000 }: InstallPromptProps) {
  const { t } = useTranslation();
  const {
    isIOS,
    isInstalled,
    canInstallNatively,
    shouldShowPrompt,
    promptInstall,
    dismissPrompt,
  } = usePWAInstall();

  const [delayPassed, setDelayPassed] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Delay showing the prompt for better UX
  useEffect(() => {
    if (!shouldShowPrompt || isInstalled) {
      return;
    }

    const timer = setTimeout(() => {
      setDelayPassed(true);
    }, delayMs);

    return () => clearTimeout(timer);
  }, [shouldShowPrompt, isInstalled, delayMs]);

  // Compute visibility from state - not shown until delay passes and conditions are met
  const visible = delayPassed && shouldShowPrompt && !isInstalled && !dismissed;

  const handleInstall = async () => {
    const result = await promptInstall();
    if (result?.outcome === 'accepted') {
      setDismissed(true);
    }
  };

  const handleDismiss = () => {
    dismissPrompt();
    setDismissed(true);
  };

  if (!visible) return null;

  // iOS-specific instructions modal
  if (isIOS) {
    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4">
        <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-6 shadow-xl animate-in slide-in-from-bottom duration-300">
          {/* App Icon */}
          <div className="mb-4 flex justify-center">
            <img
              src="/icons/icon-192.png"
              alt="Shopman"
              className="h-16 w-16 rounded-2xl shadow-lg"
            />
          </div>

          {/* Title */}
          <h2 className="mb-2 text-center text-xl font-bold text-gray-900 dark:text-white">
            {t('installApp', 'Installer Shopman')}
          </h2>

          {/* Description */}
          <p className="mb-6 text-center text-sm text-gray-600 dark:text-gray-400">
            {t('installAppDesc', 'Installer appen for hurtig adgang og offline-funktioner')}
          </p>

          {/* Instructions */}
          <div className="mb-6 space-y-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-green-500 text-white text-sm font-bold">
                1
              </div>
              <p className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                {t('tapShare', 'Tryk p\u00e5 Del-knappen')}
                <ShareIcon className="ml-2 text-blue-600 dark:text-blue-400" />
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-green-500 text-white text-sm font-bold">
                2
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {t('scrollAndTap', 'Scroll ned og tryk')} <strong>"{t('addToHomeScreen', 'F\u00f8j til hjemmesk\u00e6rm')}"</strong>
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-green-500 text-white text-sm font-bold">
                3
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {t('tapAdd', 'Tryk')} <strong>"{t('add', 'Tilf\u00f8j')}"</strong> {t('toConfirm', 'for at bekr\u00e6fte')}
              </p>
            </div>
          </div>

          {/* Dismiss Button */}
          <button
            onClick={handleDismiss}
            className="w-full rounded-xl bg-gray-200 dark:bg-gray-700 px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 transition hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            {t('maybeLater', 'M\u00e5ske senere')}
          </button>
        </div>
      </div>
    );
  }

  // Android/Desktop install banner
  if (canInstallNatively) {
    return (
      <div className="fixed bottom-20 md:bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg p-4 animate-in slide-in-from-bottom duration-300">
        <div className="max-w-md mx-auto flex items-center gap-4">
          {/* App Icon */}
          <img
            src="/icons/icon-192.png"
            alt="Shopman"
            className="w-12 h-12 rounded-xl shadow"
          />

          {/* Text */}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 dark:text-white truncate">
              {t('installApp', 'Installer Shopman')}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
              {t('installBannerDesc', 'F\u00e5 hurtig adgang fra din startsk\u00e6rm')}
            </p>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleInstall}
              className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium text-sm hover:bg-green-600 transition"
            >
              {t('install', 'Installer')}
            </button>
            <button
              onClick={handleDismiss}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
              aria-label={t('close', 'Luk')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
